// Stock History API Client
// Provides methods for accessing historical signal data and analytics
// All types aligned with backend Stock History endpoints (snake_case fields)

import { apiRequest } from './client';

// Signal types match database schema (4 types)
export type StockHistorySignal =
  | 'STRONG_BUY'
  | 'OPPORTUNITY'
  | 'BULL_TRAP'
  | 'STRONG_SELL';

export type StockHistoryDirection = 'UP' | 'DOWN';

// Signal type constants for components
export const SIGNAL_TYPES = {
  STRONG_BUY: 'STRONG_BUY' as const,
  OPPORTUNITY: 'OPPORTUNITY' as const,
  BULL_TRAP: 'BULL_TRAP' as const,
  STRONG_SELL: 'STRONG_SELL' as const,
} as const;

// User-friendly labels
export const SIGNAL_LABELS: Record<StockHistorySignal, string> = {
  STRONG_BUY: 'Strong Buy',
  OPPORTUNITY: 'Opportunity',
  BULL_TRAP: 'Bull Trap',
  STRONG_SELL: 'Strong Sell',
};

// Signal descriptions for tooltips (UX enhancement)
export const SIGNAL_DESCRIPTIONS: Record<StockHistorySignal, string> = {
  STRONG_BUY: 'High-confidence bullish signals. Multiple indicators agree.',
  OPPORTUNITY: 'Moderate-confidence bullish signals. Good risk/reward.',
  BULL_TRAP: 'False bullish signals (looks like up, but goes down). Avoid buying.',
  STRONG_SELL: 'High-confidence bearish signals. Consider exiting positions.',
};

// /api/stock-history/summary
export interface StockHistorySummaryResponse {
  total_signals: number;
  completed_7d: number;
  correct_7d: number | null;
  accuracy_7d: number | null;
  unique_symbols: number;
  first_date: string | null; // ISO date (YYYY-MM-DD)
  last_date: string | null; // ISO date (YYYY-MM-DD)
}

// /api/stock-history/win-loss
export interface StockHistoryWinLossItem {
  signal: StockHistorySignal;
  wins: number;
  losses: number;
  win_rate: number; // percentage 0-100
  total: number;
}
export type StockHistoryWinLossResponse = StockHistoryWinLossItem[];

// /api/stock-history/decay
export interface StockHistoryDecayPoint {
  horizon: string; // e.g. "1d", "3d", "7d" (trading days)
  accuracy: number; // percentage 0-100
}
export interface StockHistoryDecayItem {
  signal: StockHistorySignal;
  sampleSize: number;
  decay: StockHistoryDecayPoint[];
}
export type StockHistoryDecayResponse = StockHistoryDecayItem[];

// /api/stock-history/returns
export interface StockHistoryReturnsItem {
  signal: StockHistorySignal;
  avg_return_1d: number; // percent
  avg_return_3d: number; // percent
  avg_return_7d: number; // percent
  avg_win_7d: number | null; // percent - Average of winning trades (return_7d > 0), null if no wins
  avg_loss_7d: number | null; // percent - Average of losing trades (return_7d < 0), null if no losses
  sample_size: number;
}
export type StockHistoryReturnsResponse = StockHistoryReturnsItem[];

// /api/stock-history/stocks
export interface StockHistoryStockItem {
  symbol: string;
  signal_count: number;
  last_signal: string | null; // ISO date (YYYY-MM-DD)
  completed_7d: number;
  accuracy_7d: number | null; // percentage 0-100
  avg_return_7d: number | null; // percent
}
export type StockHistoryStocksResponse = StockHistoryStockItem[];

// /api/stock-history/:symbol
export interface StockHistoryEntry {
  symbol: string;
  date: string; // ISO date
  signal: StockHistorySignal;
  direction: StockHistoryDirection;
  return_1d: number | null;
  return_3d: number | null;
  return_7d: number | null;
}
export type StockHistoryResponse = StockHistoryEntry[];


// Cumulative performance series
export interface StockHistoryCumulativePoint {
  date: string; // ISO date
  equity: number; // portfolio equity
}
export type StockHistoryCumulativeResponse = StockHistoryCumulativePoint[];

// /api/stock-history/confidence/:symbol
export interface StockHistoryConfidenceResponse {
  symbol: string;
  sample_size: number;
  accuracy_7d: number | null;
  avg_return_7d: number | null;
}

// Summary statistics
export async function getStockHistorySummary(): Promise<StockHistorySummaryResponse> {
  return apiRequest('/api/stock-history/summary');
}

// Win/loss ratios by signal
export async function getStockHistoryWinLoss(): Promise<StockHistoryWinLossResponse> {
  return apiRequest('/api/stock-history/win-loss');
}

// Signal decay analysis (1d/3d/7d trading days)
export async function getStockHistoryDecay(): Promise<StockHistoryDecayResponse> {
  return apiRequest('/api/stock-history/decay');
}

// Average returns by signal (1d/3d/7d trading days)
export async function getStockHistoryReturns(): Promise<StockHistoryReturnsResponse> {
  return apiRequest('/api/stock-history/returns');
}

// Stock-level statistics
export async function getStockHistoryStocks(): Promise<StockHistoryStocksResponse> {
  return apiRequest('/api/stock-history/stocks');
}

// Individual stock history
export async function getStockHistory(symbol: string, limit?: number): Promise<StockHistoryResponse> {
  const params = limit ? `?limit=${limit}` : '';
  return apiRequest(`/api/stock-history/${symbol}${params}`);
}

// Stock confidence scores
export async function getStockConfidence(symbol: string): Promise<StockHistoryConfidenceResponse> {
  return apiRequest(`/api/stock-history/confidence/${symbol}`);
}

// Cumulative performance
export async function getStockHistoryCumulative(): Promise<StockHistoryCumulativeResponse> {
  return apiRequest('/api/stock-history/cumulative');
}

// /api/stock-history/health-trends
export interface HealthTrendSummary {
  snapshot_date: string;
  overall_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  total_mature_signals: number;
  total_pending_signals: number;
  total_captured_signals: number;
  healthy_signals: number;
  degraded_signals: number;
  unhealthy_signals: number;
  unknown_signals: number;
}

export interface HealthTrendsResponse {
  dateRange: {
    start: string;
    end: string;
    days: number;
  };
  summary: HealthTrendSummary[];
  signals: Record<string, any[]>;
  metadata: {
    totalSnapshots: number;
    totalDays: number;
  };
}

// Health trends
export async function getHealthTrends(days: number = 30): Promise<HealthTrendsResponse> {
  return apiRequest(`/api/stock-history/health-trends?days=${days}`);
}
